<!-- BaseModal.vue - Unified Reusable Modal Component -->
<template>
  <Teleport to="body">
    <Transition name="modal-fade">
      <div
        v-if="isVisible"
        class="fixed inset-0 z-50 overflow-y-auto max-[730px]:!overflow-hidden"
        :aria-labelledby="ariaLabelledby"
        role="dialog"
        aria-modal="true"
      >
        <!-- Background overlay -->
        <div
          class="flex items-end justify-center min-h-screen pt-0 px-0 pb-0 text-center sm:block sm:p-0 max-[730px]:!block max-[730px]:!p-0"
        >
          <Transition name="overlay-fade">
            <div
              v-if="isVisible"
              class="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-all duration-300"
              aria-hidden="true"
              @click="handleBackdropClick"
            ></div>
          </Transition>

          <!-- This element is to trick the browser into centering the modal contents. -->
          <span class="hidden sm:inline-block sm:align-middle sm:h-screen max-[730px]:!hidden" aria-hidden="true"
            >&#8203;</span
          >

          <!-- Modal panel -->
          <Transition name="modal-slide">
            <div
              v-if="isVisible"
              class="relative inline-block align-bottom bg-white dark:bg-gray-800 text-left overflow-hidden shadow-2xl transform transition-all w-full sm:my-8 sm:align-middle max-[730px]:!flex max-[730px]:!flex-col max-[730px]:!fixed max-[730px]:!inset-0 max-[730px]:!m-0 max-[730px]:!h-screen max-[730px]:!max-h-screen max-[730px]:!w-screen max-[730px]:!align-baseline"
              :class="[
                modalSize,
                modalType === 'offcanvas' ? 'fixed inset-y-0 right-0 h-full max-w-full pl-10' : 'sm:rounded-3xl min-h-screen sm:min-h-0 max-[730px]:!rounded-none max-[730px]:!min-h-screen'
              ]"
            >
              <!-- Header -->
              <div 
                v-if="showHeader"
                :class="[
                  'px-4 py-4 sm:px-6',
                  headerGradient,
                  modalType === 'offcanvas' ? 'rounded-tl-3xl' : ''
                ]"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center">
                    <!-- Icon -->
                    <div
                      v-if="icon"
                      class="flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-white/20 backdrop-blur-sm"
                    >
                      <component :is="icon" class="h-6 w-6 text-white" />
                    </div>
                    
                    <!-- Title -->
                    <div :class="icon ? 'ml-4' : ''">
                      <h3 
                        class="text-xl font-semibold text-white"
                        :id="ariaLabelledby"
                      >
                        {{ title }}
                      </h3>
                      <p 
                        v-if="subtitle" 
                        class="text-white/80 text-sm mt-1"
                      >
                        {{ subtitle }}
                      </p>
                    </div>
                  </div>
                  
                  <!-- Close button -->
                  <button
                    v-if="showCloseButton"
                    @click="$emit('close')"
                    class="p-2 rounded-lg hover:bg-white/20 transition-colors"
                    :aria-label="closeButtonLabel"
                  >
                    <X class="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              <!-- Content -->
              <div 
                :class="[
                  contentPadding,
                  scrollable ? 'overflow-y-auto' : '',
                  modalType === 'offcanvas' && showHeader ? 'flex-1' : '',
                  maxHeight,
                  'max-[730px]:!flex-1 max-[730px]:!overflow-y-auto max-[730px]:!max-h-none'
                ]"
              >
                <slot />
              </div>

              <!-- Footer -->
              <div 
                v-if="showFooter"
                class="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 px-4 py-4 sm:px-6 border-t border-gray-200 dark:border-gray-600"
              >
                <div class="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-3 space-y-3 space-y-reverse sm:space-y-0">
                  <slot name="footer" />
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { X } from 'lucide-vue-next'

export interface Props {
  isVisible: boolean
  title?: string
  subtitle?: string
  icon?: any
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | 'full'
  type?: 'default' | 'danger' | 'success' | 'warning' | 'info'
  modalType?: 'modal' | 'offcanvas'
  showHeader?: boolean
  showFooter?: boolean
  showCloseButton?: boolean
  closableByBackdrop?: boolean
  scrollable?: boolean
  contentPadding?: string
  maxHeight?: string
  ariaLabelledby?: string
  closeButtonLabel?: string
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  subtitle: '',
  icon: undefined,
  size: 'lg',
  type: 'default',
  modalType: 'modal',
  showHeader: true,
  showFooter: false,
  showCloseButton: true,
  closableByBackdrop: true,
  scrollable: true,
  contentPadding: 'px-4 py-4 sm:px-6 sm:py-6',
  maxHeight: 'max-h-[90vh]',
  ariaLabelledby: 'modal-title',
  closeButtonLabel: 'Close modal'
})

const emit = defineEmits<{
  close: []
}>()

// Computed properties for dynamic styling
const modalSize = computed(() => {
  const sizes = {
    sm: 'max-w-full sm:max-w-sm',
    md: 'max-w-full sm:max-w-md', 
    lg: 'max-w-full sm:max-w-lg',
    xl: 'max-w-full sm:max-w-xl',
    '2xl': 'max-w-full max-[730px]:!max-w-full max-[730px]:!w-full max-[730px]:!h-full max-[730px]:!m-0 max-[730px]:!rounded-none sm:max-w-2xl',
    '3xl': 'max-w-full sm:max-w-3xl',
    '4xl': 'max-w-full sm:max-w-4xl',
    full: 'max-w-full'
  }
  return sizes[props.size]
})

const headerGradient = computed(() => {
  const gradients = {
    default: 'bg-primary-gradient',
    danger: 'bg-red-gradient',
    success: 'bg-green-gradient',
    warning: 'bg-yellow-gradient',
    info: 'bg-blue-gradient'
  }
  return gradients[props.type]
})

// Handle backdrop click
const handleBackdropClick = () => {
  if (props.closableByBackdrop) {
    emit('close')
  }
}
</script>

<style scoped>
/* Animation styles */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: all 0.2s ease;
}

.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
  backdrop-filter: blur(0px);
}

.modal-slide-enter-active {
  transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.modal-slide-leave-active {
  transition: all 0.3s ease;
}

.modal-slide-enter-from {
  opacity: 0;
  transform: translateY(20px) scale(0.95);
}

.modal-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(1.02);
}

/* Offcanvas specific animations */
.offcanvas-slide-enter-active,
.offcanvas-slide-leave-active {
  transition: transform 0.3s ease-in-out;
}

.offcanvas-slide-enter-from,
.offcanvas-slide-leave-to {
  transform: translateX(100%);
}

/* Gradient backgrounds */
.bg-primary-gradient {
  background-image: var(--gradient-primary, linear-gradient(135deg, #667eea 0%, #764ba2 100%));
}

.bg-red-gradient {
  background-image: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
}

.bg-green-gradient {
  background-image: linear-gradient(135deg, #51cf66 0%, #40c057 100%);
}

.bg-yellow-gradient {
  background-image: linear-gradient(135deg, #f59e0b 0%, #b45309 100%); /* darker amber -> amber-500 to amber-700 */
}

.bg-blue-gradient {
  background-image: linear-gradient(135deg, #74c0fc 0%, #339af0 100%);
}

/* Enhanced hover effects */
.bg-primary-gradient:hover {
  background-image: linear-gradient(0deg, #0088cc 24%, #26b8ff 110%);
}

.bg-red-gradient:hover {
  background-image: linear-gradient(135deg, #f56565 0%, #e53e3e 100%);
}

.bg-green-gradient:hover {
  background-image: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
}

.bg-yellow-gradient:hover {
  background-image: linear-gradient(135deg, #d97706 0%, #92400e 100%);
}

.bg-blue-gradient:hover {
  background-image: linear-gradient(135deg, #4299e1 0%, #3182ce 100%);
}
</style>
